import React, { useState, useCallback } from 'react';
import {
  CheckCircle,
  XCircle,
  Filter,
  BrainCircuit,
  Hourglass,
} from 'lucide-react';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { FiEdit2, FiAlertTriangle } from 'react-icons/fi';

// -- Inline helpers (subset of ToolApprovalUI logic) ------------------------------------

const renderGenericArguments = (parsedArguments) => {
  const fieldMap = {
    project_identifier: 'Project Identifier',
    issue_id_or_key: 'Issue ID/Key',
    transition_id: 'Transition ID',
    transition_name: 'Transition Status',
    base_url: 'Base URL',
    repository_name: 'Repository Name',
    branch_name: 'Branch Name',
    pull_number: 'Pull Request #',
  };

  const result = {};
  const nestedFields =
    parsedArguments.fields_to_update ||
    parsedArguments.additional_fields ||
    parsedArguments.payload ||
    {};

  Object.entries(parsedArguments).forEach(([key, value]) => {
    if (
      [
        'action_description',
        'fields_to_update',
        'additional_fields',
        'payload',
      ].includes(key) ||
      key.startsWith('customfield_')
    )
      return;
    if (fieldMap[key] && value !== undefined && value !== null) {
      result[fieldMap[key]] = String(value);
    } else if (
      typeof value !== 'object' &&
      value !== undefined &&
      value !== null
    ) {
      result[key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())] =
        String(value);
    }
  });

  Object.entries(nestedFields).forEach(([key, value]) => {
    if (key.startsWith('customfield_')) return;
    if (typeof value !== 'object' && value !== undefined && value !== null) {
      result[key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())] =
        String(value);
    }
  });

  if (Object.keys(result).length === 0) return null;

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/60">
      <div className="grid grid-cols-1 gap-y-1.5">
        {Object.entries(result).map(([key, value]) => (
          <div key={key} className="flex min-w-0 text-[11px] leading-tight">
            <span className="flex-shrink-0 whitespace-nowrap font-semibold text-zinc-400 dark:text-zinc-500">
              {key}:
            </span>
            <span className="ml-1.5 flex-grow overflow-hidden break-words font-medium text-zinc-700 dark:text-zinc-300">
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// -- Inline Approval Card -----------------------------------------------

const InlineApprovalCard = ({ toolCall, onApprove, onDecline }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  let parsedArguments = {};
  try {
    parsedArguments = JSON.parse(toolCall.function?.arguments || '{}');
  } catch {}

  const actionDesc = parsedArguments.action_description;
  const toolName = toolCall.function?.name || '';

  const handleApprove = useCallback(() => {
    onApprove(toolCall.id, toolCall);
  }, [toolCall, onApprove]);

  const handleDecline = useCallback(() => {
    onDecline(toolCall.id, 'Declined by user');
  }, [toolCall.id, onDecline]);

  const handleFeedbackSubmit = useCallback(() => {
    if (!feedbackText.trim()) return;
    onDecline(toolCall.id, feedbackText.trim());
    setShowFeedback(false);
    setFeedbackText('');
  }, [feedbackText, toolCall.id, onDecline]);

  const isDeleteTool =
    toolName === 'delete_event_outlook' || toolName === 'delete_issue_in_jira';

  return (
    <div className="mx-1 mb-1 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm dark:border-zinc-700/60 dark:bg-zinc-900">
      {/* Card Header */}
      <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2 dark:border-zinc-800">
        <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">
          Approval Required
        </span>
        <span className="rounded bg-zinc-100 px-2 py-0.5 font-mono text-[10px] text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
          {toolName.replace(/_/g, ' ')}
        </span>
      </div>

      {/* Card Body */}
      <div className="px-4 py-3">
        {isDeleteTool ? (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs font-semibold text-red-600 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400">
            <FiAlertTriangle className="h-4 w-4 flex-shrink-0" />
            Are you sure you want to permanently delete this item?
          </div>
        ) : (
          <div className="space-y-2">
            {actionDesc && (
              <p className="text-xs text-zinc-600 dark:text-zinc-300">
                {actionDesc}
              </p>
            )}
            {renderGenericArguments(parsedArguments)}
          </div>
        )}

        {/* Feedback input */}
        {showFeedback && (
          <div className="mt-3">
            <textarea
              rows={2}
              placeholder="Reason for declining..."
              className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs text-zinc-800 placeholder-zinc-400 focus:border-zinc-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder-zinc-600"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Card Footer */}
      <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-4 py-2 dark:border-zinc-800">
        {showFeedback ? (
          <>
            <button
              className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400"
              onClick={() => setShowFeedback(false)}
            >
              Cancel
            </button>
            <button
              className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-600"
              onClick={handleFeedbackSubmit}
            >
              <AiOutlineClose className="h-3 w-3" /> Confirm Decline
            </button>
          </>
        ) : (
          <>
            <button
              className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-400"
              onClick={() => setShowFeedback(true)}
            >
              <FiEdit2 className="h-3 w-3" /> Feedback
            </button>
            <button
              className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-500 hover:border-red-300 hover:bg-red-100 hover:text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400"
              onClick={handleDecline}
            >
              <AiOutlineClose className="h-3 w-3" /> Decline
            </button>
            <button
              className="inline-flex items-center gap-1 rounded-lg bg-[#5652CF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#4845b0]"
              onClick={handleApprove}
            >
              <AiOutlineCheck className="h-3 w-3" /> Approve
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// -- ArgumentsDisplay ---------------------------------------------

const ArgumentsDisplay = ({ args, args_streaming }) => {
  if ((!args || Object.keys(args).length === 0) && !args_streaming) {
    return (
      <div className="py-2 pl-11 pr-4 text-xs italic text-zinc-500">
        No arguments provided.
      </div>
    );
  }

  return (
    <div className="pb-3 pl-11 pr-4 pt-1">
      <div className="custom-scrollbar max-h-[460px] overflow-y-auto rounded-md bg-black/30 p-3">
        <pre className="whitespace-pre-wrap break-words font-mono text-xs text-zinc-400">
          <code>
            {args && Object.keys(args).length > 0
              ? JSON.stringify(args, null, 2)
              : args_streaming}
          </code>
        </pre>
      </div>
    </div>
  );
};

// -- Main Component -----------------------------------------------

const ActionTrackerEmbedded = ({
  currentToolsStatus = [],
  toolCallApprovalList = [],
  isCurrent = false,
  onApprove,
  onDecline,
}) => {
  const expandedIndex = null;

  // Build a map: tool id → full toolCall object from approval list
  const approvalItemMap = React.useMemo(() => {
    const map = new Map();
    toolCallApprovalList.forEach((tc) => map.set(tc.id, tc));
    return map;
  }, [toolCallApprovalList]);

  const actions = React.useMemo(() => {
    const approvalMap = new Map(
      toolCallApprovalList.map((approval) => [
        approval.id,
        {
          approved: approval.approved,
          approvalNeeded: approval.approvalNeeded,
        },
      ]),
    );
    return currentToolsStatus
      .map((toolCall) => {
        let argumentsParsed = {};
        let gotErrorWhileParsing = false;
        try {
          argumentsParsed = JSON.parse(toolCall.function?.arguments || '{}');
        } catch (e) {
          gotErrorWhileParsing = true;
        }

        const argsToDisplay = { ...argumentsParsed };
        delete argsToDisplay.action_description;

        return {
          id: toolCall.id,
          status: toolCall.status,
          running_phase: toolCall.running_phase,
          approvalNeeded: approvalMap.get(toolCall.id)
            ? approvalMap.get(toolCall.id).approvalNeeded &&
              !approvalMap.get(toolCall.id).approved
            : false,
          action_description:
            argumentsParsed.action_description || 'Processing Action',
          tool_name: toolCall.function?.name,
          args: argsToDisplay,
          args_streaming: gotErrorWhileParsing
            ? toolCall.function?.arguments
            : null,
        };
      })
      .filter((action) => action.tool_name !== 'record_thought_process');
  }, [currentToolsStatus, toolCallApprovalList]);

  const handleToggle = () => {};

  if (actions.length === 0) return null;

  return (
    <div className="mb-4 w-full overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-lg">
      <div className="divide-y divide-zinc-800/50">
        {actions.map((action, index) => {
          const isSuccess = action.status === 'success';
          const isFailed = action.status === 'failed';
          const isRunning = action.status === 'running';
          const isExpanded = expandedIndex === index;

          let statusIcon;
          let statusTextColor;

          if (isRunning) {
            if (action.approvalNeeded) {
              statusIcon = (
                <Hourglass className="h-5 w-5 animate-pulse text-amber-400" />
              );
              statusTextColor = 'text-amber-300';
            } else {
              const phase = action.running_phase || 'calling_tool';
              switch (phase) {
                case 'filtering':
                  statusIcon = (
                    <Filter className="h-5 w-5 animate-pulse text-indigo-300" />
                  );
                  statusTextColor = 'text-cyan-300';
                  break;
                case 'analyzing':
                  statusIcon = (
                    <BrainCircuit className="h-5 w-5 animate-analyzing-glow text-indigo-400" />
                  );
                  statusTextColor = 'text-indigo-300';
                  break;
                case 'calling_tool':
                default:
                  statusIcon = (
                    <img
                      alt="Logo"
                      src="../assets/logo.png"
                      className="w-6 animate-rotatePause"
                    />
                  );
                  statusTextColor = 'text-blue-300';
                  break;
              }
            }
          } else if (isSuccess) {
            statusIcon = <CheckCircle className="h-5 w-5 text-emerald-400" />;
            statusTextColor = 'text-zinc-300';
          } else if (isFailed) {
            statusIcon = <XCircle className="h-5 w-5 text-red-400" />;
            statusTextColor = 'text-red-300';
          } else {
            // pending / queued / unknown status
            statusIcon = <Hourglass className="h-5 w-5 text-zinc-500" />;
            statusTextColor = 'text-zinc-400';
          }

          const pendingApprovalToolCall = action.approvalNeeded
            ? approvalItemMap.get(action.id)
            : null;

          return (
            <React.Fragment key={action.id || index}>
              {/* ── Tool row ── */}
              {/* eslint-disable-next-line */}
              <div
                data-tool-id={action.id}
                className={`cursor-pointer transition-colors duration-300 ${isExpanded ? 'bg-zinc-800/40' : 'hover:bg-zinc-800/40'}`}
                onClick={() => handleToggle(index)}
              >
                <div className="relative flex items-start gap-3 px-4 py-2 transition-colors duration-200 sm:items-center">
                  <div className="mb-1 mt-1 flex-shrink-0">{statusIcon}</div>

                  <div className="flex min-w-0 flex-1 flex-col gap-x-4 gap-y-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p
                        className={`truncate text-xs font-medium ${statusTextColor}`}
                      >
                        {action.action_description}
                      </p>
                    </div>
                  </div>

                  {isRunning && !action.approvalNeeded && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden bg-zinc-700/50">
                      <div
                        className="h-full origin-left bg-primary_c"
                        style={{
                          animation: 'fillProgress 30s ease-out forwards',
                        }}
                      />
                    </div>
                  )}
                </div>

                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: isExpanded ? '500px' : '0' }}
                >
                  <ArgumentsDisplay
                    args={action.args}
                    args_streaming={action.args_streaming}
                  />
                </div>
              </div>

              {/* ── Inline approval card (directly below this tool row) ── */}
              {pendingApprovalToolCall && onApprove && onDecline && (
                <div className="border-t border-zinc-800/50 bg-zinc-950/30">
                  <InlineApprovalCard
                    toolCall={pendingApprovalToolCall}
                    onApprove={onApprove}
                    onDecline={onDecline}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <style>{`
        @keyframes fillProgress {
          0%   { transform: scaleX(0); }
          10%  { transform: scaleX(0.4); }
          20%  { transform: scaleX(0.6); }
          30%  { transform: scaleX(0.7); }
          40%  { transform: scaleX(0.75); }
          50%  { transform: scaleX(0.8); }
          60%  { transform: scaleX(0.83); }
          70%  { transform: scaleX(0.86); }
          80%  { transform: scaleX(0.88); }
          90%  { transform: scaleX(0.9); }
          95%  { transform: scaleX(0.91); }
          97%  { transform: scaleX(0.92); }
          99%  { transform: scaleX(0.93); }
          100% { transform: scaleX(0.94); }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #3f3f46;
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: content-box;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #52525b; }
        .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #3f3f46 transparent; }
      `}</style>
    </div>
  );
};

export default ActionTrackerEmbedded;
